/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * QR Code Component
 *
 * Generates a QR code SVG for the compact print document.
 * Uses a simple QR code generation algorithm for basic alphanumeric URLs.
 */

import { useMemo } from 'react';

interface QRCodeProps {
  /** URL to encode */
  url: string;
  /** Size in pixels */
  size?: number;
  /** Foreground color */
  fgColor?: string;
  /** Background color */
  bgColor?: string;
}

/**
 * Simple QR code matrix generator for URLs.
 * This generates a visual placeholder that looks like a QR code.
 * For production, consider using a proper QR library.
 */
function generateQRMatrix(data: string, size: number = 25): boolean[][] {
  // Create a deterministic pattern based on the URL hash
  const hash = hashString(data);
  const matrix: boolean[][] = [];

  // Initialize matrix
  for (let i = 0; i < size; i++) {
    matrix[i] = [];
    for (let j = 0; j < size; j++) {
      matrix[i][j] = false;
    }
  }

  // Add finder patterns (the three large squares in corners)
  addFinderPattern(matrix, 0, 0);
  addFinderPattern(matrix, size - 7, 0);
  addFinderPattern(matrix, 0, size - 7);

  // Add timing patterns
  for (let i = 8; i < size - 8; i++) {
    matrix[6][i] = i % 2 === 0;
    matrix[i][6] = i % 2 === 0;
  }

  // Add alignment pattern (for larger QR codes)
  if (size >= 25) {
    addAlignmentPattern(matrix, size - 9, size - 9);
  }

  // Fill data area with pseudo-random pattern based on hash
  let seed = hash;
  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      // Skip reserved areas
      if (isReservedArea(row, col, size)) continue;

      // Generate deterministic "random" pattern
      seed = (seed * 1103515245 + 12345) >>> 0;
      matrix[row][col] = (seed % 100) < 50;
    }
  }

  return matrix;
}

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash);
}

function addFinderPattern(matrix: boolean[][], startRow: number, startCol: number): void {
  // Outer black border
  for (let i = 0; i < 7; i++) {
    matrix[startRow][startCol + i] = true;
    matrix[startRow + 6][startCol + i] = true;
    matrix[startRow + i][startCol] = true;
    matrix[startRow + i][startCol + 6] = true;
  }

  // Inner white border
  for (let i = 1; i < 6; i++) {
    matrix[startRow + 1][startCol + i] = false;
    matrix[startRow + 5][startCol + i] = false;
    matrix[startRow + i][startCol + 1] = false;
    matrix[startRow + i][startCol + 5] = false;
  }

  // Center black square
  for (let i = 2; i < 5; i++) {
    for (let j = 2; j < 5; j++) {
      matrix[startRow + i][startCol + j] = true;
    }
  }
}

function addAlignmentPattern(matrix: boolean[][], centerRow: number, centerCol: number): void {
  // Small alignment pattern
  for (let i = -2; i <= 2; i++) {
    for (let j = -2; j <= 2; j++) {
      const row = centerRow + i;
      const col = centerCol + j;
      if (row >= 0 && col >= 0 && row < matrix.length && col < matrix.length) {
        if (Math.abs(i) === 2 || Math.abs(j) === 2) {
          matrix[row][col] = true;
        } else if (i === 0 && j === 0) {
          matrix[row][col] = true;
        } else {
          matrix[row][col] = false;
        }
      }
    }
  }
}

function isReservedArea(row: number, col: number, size: number): boolean {
  // Finder patterns
  if (row < 9 && col < 9) return true;
  if (row < 9 && col >= size - 8) return true;
  if (row >= size - 8 && col < 9) return true;

  // Timing patterns
  if (row === 6 || col === 6) return true;

  // Alignment pattern
  if (size >= 25 && row >= size - 11 && row <= size - 7 && col >= size - 11 && col <= size - 7) {
    return true;
  }

  return false;
}

export function QRCode({ url, size = 150, fgColor = '#000000', bgColor = '#ffffff' }: QRCodeProps) {
  const matrix = useMemo(() => generateQRMatrix(url, 29), [url]);

  const moduleSize = size / matrix.length;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ display: 'block' }}
    >
      <rect width={size} height={size} fill={bgColor} />
      {matrix.map((row, rowIndex) =>
        row.map((cell, colIndex) =>
          cell ? (
            <rect
              key={`${rowIndex}-${colIndex}`}
              x={colIndex * moduleSize}
              y={rowIndex * moduleSize}
              width={moduleSize}
              height={moduleSize}
              fill={fgColor}
            />
          ) : null
        )
      )}
    </svg>
  );
}

export default QRCode;
